import { Capacitor } from '@capacitor/core';

/**
 * Multipart uploads that bypass CapacitorHttp on native.
 *
 * With `CapacitorHttp.enabled` (capacitor.config.ts) the bridge patches XHR/fetch and ships a FormData
 * File to native as a base64 string (readAsBinaryString + btoa → JSON over the bridge → rebuilt as
 * multipart in Java). That blows up on large videos, never emits upload progress, and sends an empty
 * part Content-Type when `file.type` is blank. The bridge keeps the WebView's original XHR methods on
 * `window.CapacitorWebXMLHttpRequest`; calling those directly sends a real browser multipart request.
 */

interface CapacitorWebXhr {
  constructor: { new (): XMLHttpRequest };
  open: XMLHttpRequest['open'];
  send: XMLHttpRequest['send'];
  setRequestHeader: XMLHttpRequest['setRequestHeader'];
}

/** Thrown when the WebView request never got an HTTP response (CORS / blocked / offline). */
export class WebXhrNetworkError extends Error {}

function getOriginalXhr(): CapacitorWebXhr | null {
  if (!Capacitor.isNativePlatform()) return null;
  const orig = (window as unknown as { CapacitorWebXMLHttpRequest?: CapacitorWebXhr }).CapacitorWebXMLHttpRequest;
  return orig?.constructor && orig.open && orig.send && orig.setRequestHeader ? orig : null;
}

/** True on native when CapacitorHttp has patched XHR and the original can be reached. */
export const canBypassCapacitorHttp = () => getOriginalXhr() !== null;

/** POST a FormData body through the unpatched WebView XHR. Resolves with a standard Response
 *  (same contract as fetch/axiosFetch — callers check `res.ok`). */
export function webXhrUpload(
  url: string,
  init: { headers: Record<string, string>; body: FormData; onUploadProgress?: (event: ProgressEvent) => void },
): Promise<Response> {
  const orig = getOriginalXhr();
  if (!orig) return Promise.reject(new WebXhrNetworkError('Original XMLHttpRequest unavailable'));

  return new Promise((resolve, reject) => {
    const xhr = new orig.constructor();
    orig.open.call(xhr, 'POST', url, true);
    Object.entries(init.headers).forEach(([k, v]) => orig.setRequestHeader.call(xhr, k, v));
    if (init.onUploadProgress) xhr.upload.onprogress = init.onUploadProgress;
    xhr.onload = () => {
      if (xhr.status === 0) {
        reject(new WebXhrNetworkError('Upload request was blocked'));
        return;
      }
      const headers = new Headers();
      (xhr.getAllResponseHeaders() || '').trim().split(/[\r\n]+/).forEach((line) => {
        const idx = line.indexOf(':');
        if (idx > 0) headers.append(line.slice(0, idx).trim(), line.slice(idx + 1).trim());
      });
      const nullBody = xhr.status === 204 || xhr.status === 205 || xhr.status === 304;
      resolve(new Response(nullBody ? null : xhr.responseText, { status: xhr.status, statusText: xhr.statusText, headers }));
    };
    xhr.onerror = () => reject(new WebXhrNetworkError('Upload network error'));
    xhr.onabort = () => reject(new WebXhrNetworkError('Upload aborted'));
    orig.send.call(xhr, init.body);
  });
}
